import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Copy, ExternalLink, Check } from "lucide-react";
import QRCode from "react-qr-code";

interface GenerateLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  onSuccess: () => void;
  initialLinkType?: 'health_data' | 'first_consultation' | 'satisfaction_survey';
}

export function GenerateLinkModal({ open, onOpenChange, patientId, onSuccess, initialLinkType }: GenerateLinkModalProps) {
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [formData, setFormData] = useState({
    link_type: 'health_data' as 'health_data' | 'first_consultation' | 'satisfaction_survey',
    expires_in_hours: 48,
    max_submissions: '',
    notes: '',
  });

  // Definir tipo inicial e focar no campo de Observações quando o modal abre com tipo pré-selecionado
  useEffect(() => {
    if (open && initialLinkType) {
      setFormData(prev => ({ ...prev, link_type: initialLinkType }));
      setTimeout(() => {
        notesTextareaRef.current?.focus();
      }, 100);
    }
  }, [open, initialLinkType]);

  const handleGenerate = async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-assisted-collection-link', {
        body: {
          patient_id: patientId,
          link_type: formData.link_type,
          expires_in_hours: formData.expires_in_hours,
          max_submissions: formData.max_submissions ? parseInt(formData.max_submissions) : null,
          notes: formData.notes || null,
        }
      });

      if (error) throw error;

      // Verificar se há erro relacionado a migration
      if (data.error === 'Migration não aplicada' || data.message?.includes('link_type não existe')) {
        toast.error(
          `Migration não aplicada: ${data.message || data.details || 'A coluna link_type não existe no banco de dados.'}`,
          { duration: 10000 }
        );
        if (data.hint) {
          console.error('Instruções:', data.hint);
        }
        return;
      }

      setGeneratedLink(data.public_url);
      toast.success("Link gerado com sucesso!");
    } catch (error: any) {
      const errorMessage = error.message || error.error || "Erro ao gerar link";
      const errorDetails = error.details || error.hint;
      
      toast.error(
        errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage,
        { duration: 10000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setGeneratedLink(null);
    setFormData({ link_type: 'health_data', expires_in_hours: 48, max_submissions: '', notes: '' });
    setCopied(false);
    onOpenChange(false);
    if (generatedLink) {
      onSuccess();
    }
  };

  const handleWhatsApp = () => {
    if (generatedLink) {
      const message = encodeURIComponent(
        `Olá! Por favor, use este link para enviar seus dados de saúde:\n\n${generatedLink}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gerar Link de Interação com o Paciente</DialogTitle>
          <DialogDescription>
            Crie um link único para o paciente enviar dados conforme o tipo abaixo
          </DialogDescription>
        </DialogHeader>

        {!generatedLink ? (
          <div className="space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select
                value={formData.link_type}
                onValueChange={(value) => setFormData({ ...formData, link_type: value as typeof formData.link_type })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="health_data">Coleta dados de saúde</SelectItem>
                  <SelectItem value="first_consultation">Primeiro atendimento</SelectItem>
                  <SelectItem value="satisfaction_survey">Pesquisa de satisfação</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duração do Link</Label>
              <Select
                value={formData.expires_in_hours.toString()}
                onValueChange={(value) => setFormData({ ...formData, expires_in_hours: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 horas</SelectItem>
                  <SelectItem value="48">48 horas</SelectItem>
                  <SelectItem value="168">7 dias</SelectItem>
                  <SelectItem value="720">30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Limite de Submissões (opcional)</Label>
              <Input
                type="number"
                placeholder="Deixe vazio para ilimitado"
                value={formData.max_submissions}
                onChange={(e) => setFormData({ ...formData, max_submissions: e.target.value })}
              />
            </div>

            <div>
              <Label>Observações (opcional)</Label>
              <Textarea
                ref={notesTextareaRef}
                placeholder="Ex: Link para envio de exames de sangue"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <Button onClick={handleGenerate} disabled={loading} className="w-full">
              {loading ? "Gerando..." : "Gerar Link"}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-center py-4 bg-white rounded-lg">
              <QRCode value={generatedLink} size={200} />
            </div>

            <div className="space-y-2">
              <Label>Link Gerado</Label>
              <div className="flex gap-2">
                <Input
                  value={generatedLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => window.open(generatedLink, '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleWhatsApp} className="flex-1" variant="outline">
                Enviar por WhatsApp
              </Button>
              <Button onClick={handleClose} className="flex-1">
                Concluir
              </Button>
            </div>

            <div className="text-sm text-muted-foreground space-y-1 p-4 bg-muted rounded-lg">
              <p><strong>Instruções:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Envie este link para o paciente</li>
                <li>O paciente poderá enviar foto, áudio, arquivo ou texto</li>
                <li>Você receberá uma notificação quando houver nova submissão</li>
                <li>Revise e decida se quer processar com IA ou descartar</li>
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
